import { apiRequest } from '../../api/client';
import { readPref, writePref } from '../../shared/clientPrefs';

export type AiDisclosureFunction =
  | 'campaign'
  | 'character_chat'
  | 'group_chat'
  | 'table_round'
  | 'helpbot'
  | 'live_audio'
  | 'stt'
  | 'image_gen'
  | 'tts'
  | 'audiobook';

export interface TransparencyEntity {
  display_name: string;
  legal_name?: string;
  hq_country?: string;
  privacy_url?: string;
  tos_url?: string;
}

export interface TransparencyPath {
  host?: TransparencyEntity;
  hosts?: TransparencyEntity[];
  target?: TransparencyEntity;
  fallbacks?: TransparencyEntity[];
  steps?: TransparencyPath[];
  router?: TransparencyEntity | null;
  zdr_status: 'verified' | 'stale' | 'none';
}

export interface AiTransparencyResponse {
  chat: TransparencyPath | null;
  moderation: {
    precheck: TransparencyPath;
    deep: {
      /** true = die gründliche Prüfung läuft auf Loraths eigenen Rechnern. */
      local: boolean;
      /** Externer Empfänger der gründlichen Prüfung, wenn sie nicht lokal läuft. */
      primary?: TransparencyPath | null;
      fallback?: TransparencyPath | null;
    };
  };
  voice?: {
    stt_batch?: TransparencyPath | null;
    stt_realtime?: TransparencyPath | null;
    live_audio?: TransparencyPath | null;
    tts?: TransparencyPath | null;
  } | null;
  images?: TransparencyPath | null;
  adult?: TransparencyPath | null;
  links: {
    privacy: string;
    terms: string;
    agb: string;
  };
  acks: Partial<Record<AiDisclosureFunction, true>>;
  generated_at: string;
}

const locallyAcked = new Set<AiDisclosureFunction>();
const RETRY_DELAYS_MS = [1_000, 5_000, 30_000] as const;
let activeUserScope: string | null = null;
let activeUserGeneration = 0;

// R9-01: Bestaetigte Aufklaerungen liegen zusaetzlich in clientPrefs (Cookie UND
// localStorage — die native iOS-App verliert reine Cookies bei jedem Kaltstart,
// siehe shared/clientPrefs.ts). Fuehrend bleibt der Server-Ack; der lokale
// Merker deckt nur die Luecken: fehlgeschlagener Ack-POST, Offline-Start oder
// ein Kaltstart, bevor der Server geantwortet hat. Ohne ihn poppte die
// Erstnutzungs-Aufklaerung (z. B. beim Mikrofon) erneut auf.
const ACK_PREF = 'ff_ai_disclosure_acks';
// EIN Pref-Eintrag fuer alle Nutzer: `<scope>|funktion,funktion`. Ein Wechsel
// des Kontos verwirft den Eintrag beim Lesen, statt je Konto ein Cookie
// anzusammeln.
const ALL_ACK_FUNCTIONS: readonly AiDisclosureFunction[] = [
  'campaign', 'character_chat', 'group_chat', 'table_round', 'helpbot',
  'live_audio', 'stt', 'image_gen', 'tts', 'audiobook',
];

function readStoredAcks(scope: string | null): AiDisclosureFunction[] {
  if (!scope) return [];
  try {
    const raw = readPref(ACK_PREF);
    if (!raw) return [];
    const separator = raw.indexOf('|');
    if (separator < 0 || raw.slice(0, separator) !== scope) return [];
    return raw.slice(separator + 1).split(',')
      .filter((value): value is AiDisclosureFunction =>
        ALL_ACK_FUNCTIONS.includes(value as AiDisclosureFunction));
  } catch {
    // Privatmodus/blockierte Website-Daten duerfen die Aufklaerung nie brechen.
    return [];
  }
}

function writeStoredAcks(): void {
  if (!activeUserScope) return;
  try {
    writePref(ACK_PREF, `${activeUserScope}|${[...locallyAcked].join(',')}`);
  } catch {
    // Siehe oben: ein blockierter Speicher darf nur den Merker kosten.
  }
}

export function setAiDisclosureAckScope(userId: number | string | null | undefined): void {
  const nextScope = userId === null || userId === undefined ? null : String(userId);
  if (nextScope === activeUserScope) return;
  activeUserScope = nextScope;
  activeUserGeneration += 1;
  locallyAcked.clear();
  for (const funktion of readStoredAcks(nextScope)) locallyAcked.add(funktion);
}

export function rememberServerAcks(acks: AiTransparencyResponse['acks'] | undefined): void {
  if (!acks) return;
  let added = false;
  for (const [funktion, acked] of Object.entries(acks)) {
    if (!acked || locallyAcked.has(funktion as AiDisclosureFunction)) continue;
    locallyAcked.add(funktion as AiDisclosureFunction);
    added = true;
  }
  if (added) writeStoredAcks();
}

export function isAiDisclosureLocallyAcked(funktion: AiDisclosureFunction): boolean {
  return locallyAcked.has(funktion);
}

/**
 * @param tier      Chat-Stufe ('standard' | 'premium') — betrifft den Textweg.
 * @param imageTier R4-03b: die GERADE gewaehlte Bildstufe. Das i steht an
 *                  Flaechen, an denen der Nutzer die Stufe eben ausgewaehlt
 *                  hat, und muss den Empfaenger DIESER Wahl nennen (Stufe 1/2
 *                  gehen nach CN, Stufe 3 in die USA). Fehlt sie, nennt der
 *                  Server den Empfaenger der zuletzt gewaehlten Stufe.
 */
export async function fetchAiTransparency(
  context: string,
  tier?: string | null,
  imageTier?: string | null,
): Promise<AiTransparencyResponse> {
  const params = new URLSearchParams({ context });
  if (tier) params.set('tier', tier);
  if (imageTier) params.set('image_tier', imageTier);
  const response = await apiRequest<AiTransparencyResponse>(
    `/api/ai-transparency?${params.toString()}`,
    { cache: 'no-store' },
  );
  rememberServerAcks(response.acks);
  return response;
}

async function postAck(
  funktion: AiDisclosureFunction,
  retryIndex = 0,
  userGeneration = activeUserGeneration,
): Promise<void> {
  if (userGeneration !== activeUserGeneration) return;
  try {
    await apiRequest('/api/ai-transparency/ack', {
      method: 'POST',
      body: { funktion },
    });
  } catch {
    const delay = RETRY_DELAYS_MS[retryIndex];
    if (
      delay === undefined
      || typeof window === 'undefined'
      || userGeneration !== activeUserGeneration
    ) return;
    window.setTimeout(() => {
      void postAck(funktion, retryIndex + 1, userGeneration);
    }, delay);
  }
}

export function rememberAndPostAiDisclosureAck(funktion: AiDisclosureFunction): void {
  locallyAcked.add(funktion);
  writeStoredAcks();
  void postAck(funktion);
}

export function __resetAiDisclosureAcksForTest(): void {
  locallyAcked.clear();
  activeUserScope = null;
  activeUserGeneration = 0;
}
